// Riichi tile identities (design 1 D4): TileId = kind * 4 + copy over 34 kinds, 1m…9m 1p…9p 1s…9s, then
// E S W N, white, green, red. Red fives are an attribute of ids 16, 52 and 88 under rule sets that have them.

export const NO_TILE = 0xffff;
export const TILE_COUNT = 136; // ids 0..135; anything else is a blank (0xffff in protobuf, 255 from canon)

/** Whether `id` names a tile, not a blank the viewer may not see. */
export const isTile = (id: number) => id >= 0 && id < TILE_COUNT;
export const RED_IDS: ReadonlySet<number> = new Set([16, 52, 88]);

export function kindOf(id: number): number {
  return id >> 2;
}

const HONOR_FILES = ["Ton", "Nan", "Shaa", "Pei", "Haku", "Hatsu", "Chun"];
const SUITS = ["Man", "Pin", "Sou"];

/** The tile set's file name (without .svg) for a tile; red fives only where the rules have them. */
export function tileFile(id: number, redFives: boolean): string {
  const k = kindOf(id);
  if (k >= 27) return HONOR_FILES[k - 27] ?? "Blank";
  const name = `${SUITS[Math.floor(k / 9)]}${(k % 9) + 1}`;
  return redFives && RED_IDS.has(id) ? `${name}-Dora` : name;
}

const HONOR_NAMES = ["East", "South", "West", "North", "White", "Green", "Red"];
const SUIT_LETTERS = ["m", "p", "s"];

/** A tile in words, as the lessons and the tile guide say it: "9 pin", "red 5 sou", "East". */
export function tileWords(id: number, redFives: boolean): string {
  const k = kindOf(id);
  if (k >= 27) return HONOR_NAMES[k - 27] ?? "";
  const n = `${(k % 9) + 1} ${SUITS[Math.floor(k / 9)]!.toLowerCase()}`;
  return redFives && RED_IDS.has(id) ? `red ${n}` : n;
}

/** A short accessible name: "5m", "red 5p", "East". */
export function tileName(id: number, redFives: boolean): string {
  const k = kindOf(id);
  if (k >= 27) return HONOR_NAMES[k - 27] ?? "?";
  const name = `${(k % 9) + 1}${SUIT_LETTERS[Math.floor(k / 9)]}`;
  return redFives && RED_IDS.has(id) ? `red ${name}` : name;
}

/** Hand order: by kind, a red five before its plain copies. */
export function sortTiles(ids: readonly number[]): number[] {
  return [...ids].sort((a, b) => kindOf(a) - kindOf(b) || (RED_IDS.has(b) ? 1 : 0) - (RED_IDS.has(a) ? 1 : 0) || a - b);
}

/** The dora a revealed indicator points at (the next kind in its suit, winds and dragons cycling). */
export function doraOf(indicator: number): number {
  const k = kindOf(indicator);
  if (k < 27) return Math.floor(k / 9) * 9 + ((k % 9) + 1) % 9;
  if (k < 31) return 27 + ((k - 27 + 1) % 4);
  return 31 + ((k - 31 + 1) % 3);
}

export const WINDS = ["East", "South", "West", "North"];
